import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'

import type { MessageKey } from '@lensmira/i18n'

import { useTranslation } from '../i18n.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import type { OnboardingView } from '../api/reads.js'

/**
 * `OPS-204`: "In-product onboarding checklist with visible progress."
 *
 * **Six rows written at signup and read by nothing.** `SignupService.activate` has seeded them
 * since the billing slice and no screen ever showed them, so a shop that signed up landed on an
 * empty work queue reading "Nothing is waiting on anyone" and had no idea what to do next.
 * `later_todo.md` 61.
 *
 * ---
 *
 * **It sits on the work queue, and disappears when it is finished.** The work queue is where a
 * shop lands and the screen a new one finds least useful, because an empty shop has nothing
 * waiting. A separate nav section would be worse: the shops that need this are the ones who do not
 * yet know the navigation exists. Once every item is ticked it stops rendering, so the screen a
 * trading shop sees is the one it had before.
 *
 * **The labels are keys here, not in the database.** `services/admin` says why on
 * `ONBOARDING_CHECKLIST`: "the labels are deliberately absent, `packages/i18n` owns the Urdu and
 * English wording, and a label in a platform table would be untranslatable." So the server sends
 * `add_branch` and this file knows what that says in the shop's language.
 *
 * **Ticking goes through the write queue**, like every other write from this client (`OFF-001a`).
 * An item ticked with no network is ticked when the network comes back, which matters because
 * setting a shop up is exactly when its connection is least settled.
 */

export interface OnboardingProps {
  readonly load: () => Promise<OnboardingView>
  /** Absent for somebody who should not be ticking anything, which is nobody today. */
  readonly canTick?: boolean
}

/**
 * The six, in the order `ONBOARDING_CHECKLIST` positions them.
 *
 * A key the server sends that this build has no wording for is rendered as itself rather than
 * dropped: a shop seeing `publish_price_book` has a worse label and the same checklist, where a
 * silently shorter list is a shop that thinks it has finished.
 */
const ITEM_LABEL: Readonly<Record<string, MessageKey>> = {
  add_branch: 'onboarding.item.addBranch',
  add_staff: 'onboarding.item.addStaff',
  add_catalogue_items: 'onboarding.item.addCatalogue',
  publish_price_book: 'onboarding.item.publishPriceBook',
  record_first_prescription: 'onboarding.item.firstPrescription',
  take_first_sale: 'onboarding.item.firstSale',
}

export function Onboarding({ load, canTick = true }: OnboardingProps): JSX.Element | null {
  const { t } = useTranslation()
  const { enqueue } = useWriteQueue()
  const [view, setView] = useState<OnboardingView | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let live = true
    void load()
      .then((loaded) => {
        if (live) setView(loaded)
      })
      .catch(() => {
        // Silent, and that is deliberate: this is a prompt, not a feature a shop is waiting on. An
        // error banner over the work queue because an onboarding read failed would be worse than
        // the prompt being absent for a minute.
        if (live) setFailed(true)
      })
    return () => {
      live = false
    }
  }, [load])

  const tick = useCallback(
    async (itemKey: string) => {
      // Optimistic, because the queue makes the write durable and a checklist that does not move
      // when you tick it reads as broken. A refusal surfaces through the queue's own status line.
      setView((current) =>
        current === null
          ? current
          : {
              ...current,
              items: current.items.map((item) =>
                item.key === itemKey ? { ...item, done: true } : item,
              ),
              done: current.items.filter((item) => item.done || item.key === itemKey).length,
            },
      )
      await enqueue({
        method: 'POST',
        path: `/onboarding/${encodeURIComponent(itemKey)}/done`,
        label: t('onboarding.queueLabel'),
        body: {},
      })
    },
    [enqueue, t],
  )

  if (failed || view === null) return null
  // Finished, so it stops being a screen. A shop that has done all six is a shop trading.
  if (view.total === 0 || view.done >= view.total) return null

  return (
    <section className="lm-onboarding" aria-labelledby="onboarding-heading">
      <h2 id="onboarding-heading" className="lm-onboarding__heading">
        {t('onboarding.heading')}
      </h2>
      {/* `OPS-204`'s "visible progress". Counts, never a percentage: `MR-04` keeps rounding to
          three places and a progress bar reading 83% is a rounded number nobody asked for. */}
      <p className="lm-onboarding__progress">
        {t('onboarding.progress', {
          done: String(view.done),
          total: String(view.total),
        })}
      </p>

      <ul className="lm-onboarding__list">
        {view.items.map((item) => {
          // Bound once: `noUncheckedIndexedAccess` does not narrow a second lookup of the same key.
          const label = ITEM_LABEL[item.key]
          return (
            <li key={item.key} className="lm-onboarding__item">
              <span className="lm-onboarding__what">
                {label === undefined ? item.key : t(label)}
              </span>
              {item.done ? (
                <span className="lm-onboarding__done">{t('onboarding.done')}</span>
              ) : canTick ? (
                <button
                  type="button"
                  className="lm-button lm-button--secondary"
                  onClick={() => {
                    void tick(item.key)
                  }}
                >
                  {t('onboarding.tick')}
                </button>
              ) : null}
            </li>
          )
        })}
      </ul>
    </section>
  )
}
